import useAnalyticsCalls from '@/hooks/use-analytics';
import { motion } from 'motion/react';
import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import DataCategoryCard from './data-category-card';
import DataCategoryCardSkeleton from './data-category-card-skeleton';
import ExpandedDataCategoryCard from './expanded-data-category-card';
import { DataCategoryModulesProps, DataCategoryModulesRef, ExtendedCategoryProps } from './types';

// Re-export types for backward compatibility
export type { DataCategoryModulesProps, DataCategoryModulesRef, ExtendedCategoryProps };

export const DataCategoryModules = forwardRef<DataCategoryModulesRef, DataCategoryModulesProps>(
  ({ categories, isEditMode = false, isInitiallyFetching = false, labOrderStatus }, ref) => {
    const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set());
    const categoryRefs = useRef<(HTMLDivElement | null)[]>([]);
    const { callHealthDataCategoryToggled } = useAnalyticsCalls();

    // Expose methods to parent component via ref
    useImperativeHandle(
      ref,
      () => ({
        scrollToAndExpandCategory: (categoryName: string) => {
          const categoryIndex = categories.findIndex((cat) => cat.name === categoryName);

          if (categoryIndex >= 0) {
            // Expand the category if it has subcategories
            const category = categories[categoryIndex];

            if (isEditMode && category.subcategories && category.subcategories.length > 0) {
              setExpandedCards(new Set([categoryIndex]));
            }

            // Scroll to the category after expansion re-render
            setTimeout(() => {
              const categoryElement = categoryRefs.current[categoryIndex];
              if (categoryElement) {
                categoryElement.scrollIntoView({
                  behavior: 'smooth',
                  block: 'center',
                });
              }
            }, 100);
          }
        },
      }),
      [categories, isEditMode],
    );

    const toggleExpanded = useCallback(
      (index: number) => {
        setExpandedCards((prev) => {
          const newSet = new Set(prev);
          const isExpanding = !newSet.has(index);

          if (isExpanding) {
            newSet.add(index);
            // Track analytics only when expanding (opening) the category
            const categoryName = categories[index]?.name;
            if (categoryName) {
              callHealthDataCategoryToggled(categoryName, isEditMode);
            }
          } else {
            newSet.delete(index);
          }

          return newSet;
        });
      },
      [categories, callHealthDataCategoryToggled, isEditMode],
    );

    const handleKeyDown = (event: React.KeyboardEvent, index: number) => {
      const category = categories[index];
      const canExpand = isEditMode && category.subcategories && category.subcategories.length > 0;

      if (!canExpand) return;

      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        toggleExpanded(index);
      }
    };

    return (
      <div className="dashboard-reset lg:w-full">
        <motion.div
          className="grid gap-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, staggerChildren: 0.1 }}
          role="list"
          aria-label="Health data categories"
        >
          {isInitiallyFetching
            ? Array.from({ length: 3 }, (_, index) => (
                <div key={`skeleton-${index}`} className="h-full w-full" role="listitem">
                  <DataCategoryCardSkeleton />
                </div>
              ))
            : categories.map((category, index) => {
                const isExpanded = expandedCards.has(index);
                const canExpand = isEditMode && category.subcategories && category.subcategories.length > 0;

                return (
                  <div
                    key={index}
                    ref={(el) => (categoryRefs.current[index] = el)}
                    id={category.name}
                    className="h-full w-full"
                    role="listitem"
                    tabIndex={canExpand ? 0 : -1}
                    onKeyDown={canExpand ? (e) => handleKeyDown(e, index) : undefined}
                    aria-label={canExpand ? `${category.name} - Press Enter or Space to expand` : category.name}
                  >
                    {canExpand ? (
                      <ExpandedDataCategoryCard
                        {...category}
                        subcategories={category.subcategories || []}
                        isExpanded={isExpanded}
                        onToggleExpanded={() => toggleExpanded(index)}
                        aria-expanded={isExpanded}
                        aria-controls={`category-${index}-content`}
                      />
                    ) : (
                      <DataCategoryCard {...category} isEditMode={isEditMode} labOrderStatus={labOrderStatus} />
                    )}
                  </div>
                );
              })}
        </motion.div>
      </div>
    );
  },
);

DataCategoryModules.displayName = 'DataCategoryModules';

export default DataCategoryModules;
